import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MobileDeviceIcon } from '@assets/index'
import { onEnterKeyPressAndClick } from '@utils/helpers'
import './PinAndPair.scss'
import { Header } from '@components/Header/Header'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import { PinAndPairStep } from '@components/PinAndPairStep/PinAndPairStep'
import QRCode from 'react-qr-code'
import { fetchLoginCode, fetchCustomToken, loginWithCustomToken } from '@services/pinAndPair'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import { formatLoginCode } from '@utils/pinAndPair.util'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import { getLanguage } from '@utils/vocab'
import { fetchMenu } from '@actions/menuActions'
import { useDispatch } from 'react-redux'
import { ErrorCodes, ErrorActions, LocalStorageItem } from '@constants/index'
import { setError } from '@actions/errorCodeActions'
import { VelvetSDK } from 'sdk'
import useGoBackNavigation from '@hooks/useGoBackNavigation'
import { invalidCodeMessage } from '@constants/api.const'
import { routes } from '@constants/routes.const'

interface PinAndPairProps {
  showMenu: (status: boolean) => void
}

interface LoginData {
  loginCode: number
  websiteLink: string
}

export const PinAndPair = ({ showMenu }: PinAndPairProps) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [loginData, setLoginData] = useState<LoginData | null>()
  const [isCustomTokenAvailable, setIsCustomTokenAvailable] = useState(false)
  const [pinAndPairCodeError, setPinAndPairCodeError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const buttonLogInWithRemote = useRef<HTMLButtonElement>(null)
  const continueButtonRef = useRef<HTMLButtonElement>(null)
  const customTokenIntervalDuration = 15000
  const V = VelvetSDK.Vocabulary.vocabulary
  const config = VelvetSDK.Configuration.configuration
  const menuFetchCondition = VelvetSDK.CMS.isMenuFetching

  useGoBackNavigation()

  const navigateBack = useCallback(() => navigate(-1), [navigate])

  const navigateToManageProfilesPage = useCallback(() => {
    navigate(routes.MANAGE_PROFILES)
  }, [navigate])

  const getLoginData = async () => {
    try {
      const response = await fetchLoginCode()
      if (response) {
        setLoginData(response as LoginData)
        setIsLoading(false)
      }
    } catch (err) {
      return err
    }
  }

  const handleNewCodeGeneration = useCallback((e: React.KeyboardEvent | React.MouseEvent) => {
    onEnterKeyPressAndClick(e, () => {
      setPinAndPairCodeError(false)
      getLoginData()
      buttonLogInWithRemote?.current?.focus()
    })
  }, [])

  useEffect(() => {
    showMenu(false)
    if (!isLoading && buttonLogInWithRemote.current) {
      buttonLogInWithRemote.current.focus()
    }
    return () => {
      showMenu(true)
      setPinAndPairCodeError(false)
    }
  }, [showMenu, isLoading])

  useEffect(() => {
    getLoginData()
  }, [])

  const fetchVocabulary = useCallback(
    async (language?: string | null) => {
      await VelvetSDK.Vocabulary.service.fetchVocabulary(language)

      if (VelvetSDK.Vocabulary.vocabulary.error) {
        const error = VelvetSDK.Vocabulary.vocabulary.error
        const errorUpdate = {
          showErrorModal: true,
          errorCode: ErrorCodes.FETCH_VOCABULARY,
          HTTPcode: error.status,
          fetchAction: ErrorActions.VOCABULARY,
        }
        dispatch(setError(errorUpdate))
      }
    },
    [dispatch],
  )
  useEffect(() => {
    const getCustomToken = async () => {
      if (loginData?.loginCode) {
        try {
          const response = await fetchCustomToken(loginData.loginCode)
          if (response?.customToken && config?.apiEndpointBaseUrl) {
            clearInterval(customTokenRetrievalInterval)
            setIsCustomTokenAvailable(true)
            await loginWithCustomToken(response.customToken)

            const userObject = JSON.parse(localStorage.getItem(LocalStorageItem.USER_DATA) as string)

            if (userObject) {
              setPinAndPairCodeError(false)
              const language = await getLanguage()
              menuFetchCondition && (await dispatch(fetchMenu(language)))
              await fetchVocabulary(language)
              navigateToManageProfilesPage()
            }
          } else if (response?.error?.message === invalidCodeMessage) {
            setPinAndPairCodeError(true)
            clearInterval(customTokenRetrievalInterval)
          }
        } catch (err) {
          setPinAndPairCodeError(true)
          clearInterval(customTokenRetrievalInterval)
        }
      }
    }

    getCustomToken()
    const customTokenRetrievalInterval = setInterval(getCustomToken, customTokenIntervalDuration)

    return () => clearInterval(customTokenRetrievalInterval)
    // Needs "menuFetchCondition" to be excluded from the dep.array because it re-fetches the customToken API
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.apiEndpointBaseUrl, dispatch, fetchVocabulary, loginData?.loginCode, navigateToManageProfilesPage])

  useEffect(() => {
    if (pinAndPairCodeError) {
      continueButtonRef?.current?.focus()
    }
  }, [pinAndPairCodeError])

  if (isLoading) return <DotSpinner />

  return (
    <div className="pin-and-pair-container">
      <Header label="" />
      {isCustomTokenAvailable && (
        <div className="pin-and-pair-overlay">
          <DotSpinner />
        </div>
      )}
      <div className="pin-and-pair-content">
        <Text tag={TextTag.P} className="pin-and-pair-title">
          {V?.pinAndPairSteps}
        </Text>
        <div className="steps">
          <div className="pin-and-pair-steps-container">
            <PinAndPairStep
              stepNumber={V?.pinAndPairStep1}
              label={V?.pinpairDescriptionStepOneLabel}
              value={loginData?.websiteLink}
              valueClassName="url"
            />
            <PinAndPairStep
              stepNumber={V?.pinAndPairStep2}
              label={V?.pinpairDescriptionStepTwoLabel}
              value={formatLoginCode(loginData?.loginCode)}
              valueClassName="code"
            />
            <PinAndPairStep stepNumber={V?.pinAndPairStep3} label={V?.pinpairDescriptionStepThreeLabel} />
          </div>
          <div className="qr-code">
            <Text tag={TextTag.P} className="qr-container">
              {loginData?.websiteLink && <QRCode value={loginData.websiteLink} />}
            </Text>
          </div>
        </div>
        <div className="login-with-device-info">
          <MobileDeviceIcon className="login-with-device-icon" />
          <Text tag={TextTag.P} className="login-with-device-label">
            {V?.pinAndPairContinueOnDevice}
          </Text>
        </div>
        {pinAndPairCodeError && (
          <div className="pin-and-pair-overlay">
            <div className="code-expired">
              <Text tag={TextTag.P} className="code-expired-message">
                {V?.pinAndPairExpiredCode}
              </Text>
              <Text tag={TextTag.P} className="code-expired-instructions">
                {V?.pinAndPairExpiredCodeInstruction}
              </Text>
              <SecondaryButton onClick={handleNewCodeGeneration} buttonRef={continueButtonRef} onFocusDown>
                {V?.continueButtonLabel}
              </SecondaryButton>
            </div>
          </div>
        )}
        <div className="button-container">
          <Text tag={TextTag.P}>{V?.pinpairAlternativeLoginLabel}</Text>
          <SecondaryButton onClick={navigateBack} buttonRef={buttonLogInWithRemote}>
            {V?.pinpairAlternativeLoginButtonLabel}
          </SecondaryButton>
        </div>
      </div>
    </div>
  )
}
